<script lang="ts">
	import type { DateRange } from '$lib/components/pois/types';
	import type { DatasheetPoiData, GetYourGuideTour } from '../types';
	import { page as pageStore } from '$app/state';
	import Header from '$lib/components/header/Header.svelte';
	import { computeSearchParamsItinerary } from '$lib/components/itinerary/util';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import Carousel from '$lib/components/molecules/carousel/Carousel.svelte';
	import { poiScheme } from '$lib/components/pois/constants';
	import DatasheetContent from '$lib/components/pois/datasheet/DatasheetContent.svelte';
	import SideBar from '$lib/components/pois/datasheet/side-bar/SideBar.svelte';
	import DatasheetToolbar from '$lib/components/pois/datasheet/toolbar/DatasheetToolbar.svelte';
	import { getBookUrl, isDatasheetSideBarPoiData } from '$lib/components/pois/utils';
	import { isAnyPoiPartner } from '$lib/constants/sponsoredPoi';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import {
		getPoiEventGetYourGuidePayload,
		getPoiEventPayload,
		getPoiPartnerTrackingEvent,
	} from '$lib/services/tracking/googleAnalytics/pois';
	import { getBookLeadPayload, metaTrackBookLead } from '$lib/services/tracking/metaPixel/tracking';
	import { getSearchParamsStore, gotoWithParams } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';
	import { watch, watchOnce } from 'runed';
	import { get } from 'svelte/store';

	const TITLE_HEIGHT = 32;
	const HANDLE_HEIGHT = 20;
	const SCROLL_MARGIN = 68;
	const THRESHOLD = TITLE_HEIGHT + HANDLE_HEIGHT + SCROLL_MARGIN;

	interface Props {
		poi: DatasheetPoiData;
		onSelectDates?: (datesRange: DateRange) => void;
		onClearDates?: () => void;
		onClose?: () => void;
	}

	const { poi, onSelectDates, onClearDates, onClose }: Props = $props();

	let datasheetHighlightContainer: HTMLDivElement | undefined = $state();
	let isCollapsed = $state(true);
	let isCarouselOpen = $state(false);

	const searchParams = getSearchParamsStore();
	const mapLayoutContext = getMapLayoutContext();
	const {
		poiCoordinates,
		closeDatasheet,
		drawerScrollPosition,
		itineraryStore,
		moveDrawer,
	} = mapLayoutContext;

	function updatePoiCoordinatesAndCloseDatasheet() {
		track({
			action: 'datasheet_see_poi_on_map',
			payload: { action: 'click', ...getPoiEventPayload(poi) },
		});
		$poiCoordinates = poi.coordinates;
		closeDatasheet();
	}

	async function handleItineraryButtonClick() {
		track({
			action: 'datasheet_itinerary_button',
			payload: { action: 'click', ...getPoiEventPayload(poi) },
		});

		itineraryStore.addToItinerary({ coordinates: poi.coordinates, label: poi.name });
		const itinerary = await computeSearchParamsItinerary(get(itineraryStore.itinerary));

		if (itineraryStore.isCandidateForComputeItinerary()) {
			$searchParams.itinerary = itinerary;
		} else {
			gotoWithParams('/routes', { ...$searchParams, itinerary });
		}
	}

	function getTheForkUrl(poi: DatasheetPoiData) {
		if (poi.__typename === 'RestaurantPOI' || poi.__typename === 'RestaurantMichelinPOI') {
			return poi.theForkUrl ?? undefined;
		}
	}

	watch(() => $drawerScrollPosition, () => {
		if (datasheetHighlightContainer) {
			isCollapsed = $drawerScrollPosition - datasheetHighlightContainer?.offsetTop - datasheetHighlightContainer?.offsetHeight <= THRESHOLD;
		}
	});
	const CAROUSEL_OPEN_CLASSES = $derived(isCarouselOpen ? '' : 'translate-y--100%');
	const COLLAPSE_STATE_CLASSES = $derived(isCollapsed
		? 'rd-2 delay-500'
		: `rd-0 top-2 md:top-0 ${CAROUSEL_OPEN_CLASSES}`);
	const CAROUSEL_CONTAINER_COLLAPSE_STATE_CLASSES = $derived(isCollapsed
		? `rd-2 translate-y-100%`
		: `rd-0 translate-y-0`);
	const CAROUSEL_CONTAINER_CLASSES = $derived(!isCarouselOpen
		? `transition-transform transform-gpu duration-500 md:translate-y-0 ${CAROUSEL_CONTAINER_COLLAPSE_STATE_CLASSES}`
		: '');

	const datasheetContainerClass = $derived(isDatasheetSideBarPoiData(poi) ? 'md:grid' : 'flex flex-col');

	function trackDatasheetBottomsheet(isCollapsed: boolean) {
		if (!isCollapsed) {
			track({
				action: 'datasheet_bottomsheet',
				payload: {
					action: 'scroll',
					...getPoiEventPayload(poi),
				},
			});
		}
	}

	watch(() => isCollapsed, () => {
		trackDatasheetBottomsheet(isCollapsed);
	});

	function handleSideBarBookClick(getYourGuideTour: GetYourGuideTour) {
		track({
			action: 'call_to_action',
			payload: {
				cta_type: 'datasheet_book',
				...getPoiEventPayload(poi),
				...getPoiEventGetYourGuidePayload(getYourGuideTour),
			},
		});
		metaTrackBookLead(getBookLeadPayload(poi));
	}
	let position: number | 'opened' = $state('opened');

	watchOnce(() => position, () => {
		position = 232;
	});

	function getPosition() {
		if (!isCollapsed) {
			return 84; // approx height of handle + title
		} else if (position === 84 && isCollapsed) {
			return 272;
		} else if (
			poi.__typename !== 'ServiceEVPOI'
			&& poi.__typename !== 'ServiceRestStopPOI'
			&& poi.images.length > 0
			&& isCollapsed
		) {
			return 466; // approx height of title + highlight + description
		} else {
			return 'opened';
		}
	}

	function handleSnapHandleClick() {
		position = getPosition();
		moveDrawer(position, { behavior: 'smooth' });
	}
</script>

<div
	class="flex flex-col relative top-0 w-full gap-4 bg-neutral-weakest md:bg-neutral-weak"
>
	<div class="hidden md:block">
		<Header
			hasRoleBanner
			variant="no-logo"
			leftButton={{ text: m.poi_datasheet__back_to_list(), icon: 'west' }}
			class="md:rd-2 md:bg-neutral-weakest md:shadow-smooth-block md:py-2"
			onLeftButtonClick={onClose}
		/>
	</div>
	<div class={['grid-cols-[minmax(0,1fr)_300px] rd-2 md:pb-0 flex-1 gap-2 w-full', datasheetContainerClass]}>
		<div class="flex-1 rd-2">
			{#if poi.__typename !== 'ServiceEVPOI' && poi.__typename !== 'ServiceRestStopPOI' && poi.images.length > 0}
				<div
					class={['w-full h-70 absolute md:relative md:mb-4 md:block md:rd-2', COLLAPSE_STATE_CLASSES]}
				>
					<div class={CAROUSEL_CONTAINER_CLASSES}>
						<Carousel
							carouselClasses={isCollapsed ? 'rd-2' : 'rd-0'}
							enableIndicators
							enableFullscreen
							enableImageCopyright
							navigationType="minimal"
							images={poi.images}
							prefixAlt={`${poi.name} - ${poi.address.city || ''}`}
							bind:isCarouselOpen
							onImageClick={(carouselOpen: boolean) => {
								!carouselOpen
									&& track({
										action: 'datasheet_picture',
										payload: {
											action: 'click',
											...getPoiEventPayload(poi),
										},
									});
							}}
							onImageScroll={() => {
								track({
									action: 'datasheet_picture',
									payload: {
										action: 'scroll',
										...getPoiEventPayload(poi),
									},
								});
							}}
							navigationIcons={{ left: 'west', right: 'east' }}
						/>
					</div>
				</div>
				<!-- Add fixed snappoint that does not move for the image container -->
				<div class="snap relative md:hidden"></div>
				<!-- cover element to avoid image flickering through -->
				<div class="w-full h-70 absolute bg-neutral-weakest rd-2 md:hidden"></div>
			{/if}
			<div class="flex flex-col gap-4 bg-neutral-weakest rd-2 relative md:shadow-smooth-block">
				<DatasheetContent
					{poi}
					{isCollapsed}
					bind:datasheetHighlightContainer
					onItineraryClick={handleItineraryButtonClick}
					onLearnMoreClick={() => {
						track(getPoiPartnerTrackingEvent(poi));
						metaTrackBookLead(getBookLeadPayload(poi));
					}}
					onSeeOnMap={updatePoiCoordinatesAndCloseDatasheet}
					onHandleClick={handleSnapHandleClick}
				/>
				{#if !isAnyPoiPartner(poi)}
					<div class="sticky flex bottom-0 left-0 w-full bg-neutral-weakest rounded-b-2xl">
						<DatasheetToolbar
							showItineraryButton
							scheme={poiScheme[poi.__typename]}
							isFloating={!isCollapsed}
							bookUrl={getBookUrl(poi, pageStore, $searchParams)}
							theForkUrl={getTheForkUrl(poi)}
							infoUrl={poi.__typename === 'ServiceRestStopPOI' && poi.infoUrl ? poi.infoUrl : undefined}
							onItineraryClick={handleItineraryButtonClick}
							onBookClick={() => {
								track({
									action: 'call_to_action',
									payload: { cta_type: 'datasheet_book', ...getPoiEventPayload(poi) },
								});
								metaTrackBookLead(getBookLeadPayload(poi));
							}}
							onLearnMoreClick={() => {
								track(getPoiPartnerTrackingEvent(poi));
								metaTrackBookLead(getBookLeadPayload(poi));
							}}
						/>
					</div>
				{/if}
			</div>
		</div>

		{#if isDatasheetSideBarPoiData(poi)}
			<SideBar
				class="mb-15"
				{poi}
				onBookClick={handleSideBarBookClick}
				onSelectDates={onSelectDates}
				{onClearDates}
			/>
		{/if}
	</div>
</div>

<style>
	.snap {
		scroll-snap-align: start;
		scroll-snap-stop: always;
		scroll-margin: 272px;
	}
</style>
